import React from "react"
import { graphql } from "gatsby"
import { Box, Container, Typography } from "@mui/material"
import Seo from "../components/Seo"
import YoutubeEmbed from "../components/YoutubeEmbed"
import { BLOCKS, MARKS } from "@contentful/rich-text-types"
import { documentToReactComponents } from "@contentful/rich-text-react-renderer"
import { GatsbyImage } from "gatsby-plugin-image"
import { useLocation } from "@reach/router"
import HeroImage from "../components/HeroImage/HeroImage"

const smallWidth = "53%"
const mediumWidth = "68%"
const fullWidth = "100%"

const small = {
  xs: fullWidth,
  sm: fullWidth,
  md: mediumWidth,
  lg: smallWidth,
  xl: smallWidth,
}
const medium = {
  xs: fullWidth,
  sm: fullWidth,
  md: fullWidth,
  lg: mediumWidth,
  xl: mediumWidth,
}
const full = {
  xs: fullWidth,
  sm: fullWidth,
  md: fullWidth,
  lg: fullWidth,
  xl: fullWidth,
}

const getOptions = (references) => ({
  renderMark: {
    [MARKS.BOLD]: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
  },
  renderNode: {
    [BLOCKS.HEADING_2]: (node, children) => (
      <Typography
        variant="h2"
        sx={{
          marginTop: 4,
          width: small,
          textAlign: "left",
        }}
      >
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_3]: (node, children) => (
      <Typography
        variant="h3"
        sx={{
          marginTop: 4,
          width: small,
          textAlign: "left",
        }}
      >
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_4]: (node, children) => (
      <Typography
        variant="h4"
        sx={{
          marginTop: 4,
          width: small,
          textAlign: "left",
        }}
      >
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_5]: (node, children) => (
      <Typography
        variant="h5"
        sx={{
          marginTop: 4,
          width: small,
          textAlign: "left",
        }}
      >
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_6]: (node, children) => (
      <Typography
        variant="h6"
        sx={{
          marginTop: 4,
          width: small,
          textAlign: "left",
        }}
      >
        {children}
      </Typography>
    ),
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <Typography
        variant="body1"
        sx={{
          marginTop: 2,
          width: { sm: "100%", md: "100%", lg: smallWidth, xl: smallWidth },
        }}
      >
        {children}
      </Typography>
    ),
    [BLOCKS.QUOTE]: (node, children) => (
      <Typography
        variant="windsor"
        component="blockquote"
        sx={{
          marginTop: 3,
          marginBottom: 3,
          width: {
            xs: fullWidth,
            sm: "100%",
            md: "100%",
            lg: mediumWidth,
            xl: mediumWidth,
          },
          "& > p": {
            color: "titles.main",
            width: `100%`,
            textAlign: `center`,
            fontSize: `2rem`,
            fontFamily: `"WindsorPro-Regular", sans-serif`,
            textTransform: `uppercase`,
          },
        }}
      >
        {children}
      </Typography>
    ),
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      const entryId = node.data.target.sys.id
      const entryRef = references.filter(
        ({ contentful_id }) => contentful_id === entryId
      )[0]
      const isVideo =
        entryRef && entryRef.sys.contentType.sys.id === "videoEmbed"
      if (isVideo) {
        return <YoutubeEmbed width={medium} {...entryRef} />
      }
      return <Typography>Embed error</Typography>
    },
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const imageId = node.data.target.sys.id
      const image = references.filter(
        ({ contentful_id }) => contentful_id === imageId
      )[0]
      const { imageData, title, description } = {
        imageData: image ? image.gatsbyImageData : null,
        title: image ? image.title : "",
        description: image ? image.description : "",
      }
      return (
        <Box sx={{ width: medium }}>
          <GatsbyImage
            image={imageData}
            alt={`${title}${description ? ` - ${description}` : ""}`}
            style={{
              width: "100%",
              marginTop: "32px",
              marginBottom: "32px",
              padding: 0,
            }}
          />
        </Box>
      )
    },
  },
})

export default function JournalPost({ data }) {
  const { title, body, introduction, featuredImage } = data.contentfulJournal
  const location = useLocation()

  return (
    <Box sx={{ paddingBottom: 12 }}>
      <Seo
        title={title}
        pathname={location.pathname}
        article={null}
        image={featuredImage.gatsbyImageData.images.fallback.src}
        description={introduction ? introduction.introduction : ""}
      />
      <Container
        maxWidth={false}
        sx={{ padding: { md: 0, xs: 0, lg: 0, xl: 0 } }}
      >
        <HeroImage
          image={featuredImage.gatsbyImageData}
          alt={title}
          style={{
            height: `auto`,
            marginBottom: 6,
            maxHeight: `70vh`,
          }}
        />

        <Container
          maxWidth="xl"
          sx={{
            paddingLeft: { xs: 2, md: "0", lg: "0", xl: "0" },
            paddingRight: { xs: 2, md: "0", lg: "0", xl: "0" },
            paddingTop: { xs: 3, md: 3, lg: 3, xl: 3 },
            display: `flex`,
            flexDirection: `column`,
            alignItems: `center`,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              textAlign: `center`,
              color: `titles.main`,
              width: small,
            }}
          >
            {title}
          </Typography>
          {introduction && (
            <Typography
              sx={{
                fontSize: `1.2rem`,
                color: `titles.main`,
                marginBottom: 6,
                marginTop: 6,
                width: medium,
              }}
            >
              {introduction.introduction}
            </Typography>
          )}
        </Container>
        <Container
          maxWidth="xl"
          sx={{
            display: `flex`,
            flexDirection: `column`,
            alignItems: `center`,
            padding: { xs: 2, md: "0", lg: "0", xl: "0" },
          }}
        >
          {documentToReactComponents(
            JSON.parse(body.raw),
            getOptions(body.references)
          )}
        </Container>
      </Container>
    </Box>
  )
}

export const journalQuery = graphql`
  query ($slug: String!) {
    contentfulJournal(slug: { eq: $slug }) {
      id
      title
      introduction {
        introduction
      }
      slug
      featuredImage {
        gatsbyImageData(
          width: 1200
          placeholder: BLURRED
          formats: [AUTO, WEBP]
        )
      }
      body {
        raw
        references {
          ... on ContentfulAsset {
            contentful_id
            title
            description
            __typename
            gatsbyImageData(
              layout: FULL_WIDTH
              placeholder: BLURRED
              formats: [AUTO, WEBP]
            )
          }
          ... on ContentfulVideoEmbed {
            contentful_id
            title
            url
            sys {
              contentType {
                sys {
                  id
                }
              }
            }
          }
        }
      }
    }
  }
`
